import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export interface PortfolioSiteLayoutProps {
  portfolioSlug: string;
  siteTitle: string;
  tagline?: string;
  avatarUrl?: string | null;
  avatarAlt?: string;
  footerDomain?: string;
  showBlogLink?: boolean;
  showVideoLink?: boolean;
  showPricingLink?: boolean;
  children: React.ReactNode;
}

function scrollToSection(sectionId: string) {
  window.requestAnimationFrame(() => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

const PortfolioSiteLayout: React.FC<PortfolioSiteLayoutProps> = ({
  portfolioSlug,
  siteTitle,
  tagline,
  avatarUrl,
  avatarAlt,
  footerDomain,
  showBlogLink = true,
  showVideoLink = false,
  showPricingLink = false,
  children,
}) => {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const base = `/${encodeURIComponent(portfolioSlug)}`;

  const segments = location.pathname.split('/').filter(Boolean);
  const firstDecoded = segments[0] ? decodeURIComponent(segments[0]) : '';
  const slugDecoded = decodeURIComponent(portfolioSlug);
  const isPortfolioHome = segments.length === 1 && firstDecoded === slugDecoded;

  const navigateToSection =
    (sectionId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setNavOpen(false);
      const hash = `#${sectionId}`;
      const targetUrl = `${base}${hash}`;

      if (isPortfolioHome) {
        scrollToSection(sectionId);
        if (typeof window.history.replaceState !== 'undefined') {
          window.history.replaceState(null, '', targetUrl);
        }
        return;
      }

      void navigate({ pathname: base, hash });
    };

  const footLabel = footerDomain
    ? `${footerDomain} · white labeled portfolio`
    : `${siteTitle} · powered by Arrangelly`;

  return (
    <div className="public-site-page">
      <div className="public-site">
        <header className="pub-nav">
          <Link to={base} className="pub-nav-brand" onClick={() => setNavOpen(false)}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={avatarAlt ?? siteTitle} className="pub-nav-avatar" />
            ) : (
              <div className="pub-nav-avatar pub-nav-avatar--placeholder" aria-hidden />
            )}
            <span className="min-w-0">
              <span className="pub-brand">{siteTitle}</span>
              {tagline && <span className="pub-brand-tag">{tagline}</span>}
            </span>
          </Link>

          <button
            type="button"
            className="pub-menu-btn"
            aria-expanded={navOpen}
            aria-label="Menu"
            onClick={() => setNavOpen((o) => !o)}
          >
            Menu
          </button>

          <nav className={`pub-links${navOpen ? ' is-open' : ''}`} aria-label="Site">
            <a href={`${base}#about`} onClick={navigateToSection('about')}>
              About
            </a>
            {showPricingLink && (
              <a href={`${base}#pricing`} onClick={navigateToSection('pricing')}>
                Plans
              </a>
            )}
            {showBlogLink && (
              <a href={`${base}#blog`} onClick={navigateToSection('blog')}>
                Blog
              </a>
            )}
            {showVideoLink && (
              <a href={`${base}#video`} onClick={navigateToSection('video')}>
                Videos
              </a>
            )}
            <a href={`${base}#contact`} onClick={navigateToSection('contact')}>
              Book
            </a>
          </nav>
        </header>

        {children}

        <footer className="pub-foot">{footLabel}</footer>
      </div>
    </div>
  );
};

export default PortfolioSiteLayout;
